import { friendlyDbError } from '../utils/friendlyDbError';
/**
 * Partner Pipeline Store
 *
 * Store Zustand per la pipeline Kanban dei partner (external_partners).
 * Mirror del modello lead_pipelines/lead_pipeline_stages (vedi leadPipelineStore.ts),
 * con membership su partner_pipeline_memberships (mirror di client_pipeline_memberships).
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import type { PartnerPipeline, PartnerPipelineStage, PartnerPipelineMembership } from '../types';

// Recupera organization_id + user_id dell'utente corrente (stesso pattern di partnerStore.ts/partnerNoteStore.ts)
async function getOrgAndUserId(): Promise<{ organizationId: string; userId: string }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!userData.user) throw new Error('User not authenticated');

  const { data: profileData, error: profileError } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();

  if (profileError) throw profileError;
  if (!profileData?.organization_id) throw new Error('Organization ID not found for current user');

  return { organizationId: profileData.organization_id, userId: userData.user.id };
}

interface PartnerPipelineState {
  pipelines: PartnerPipeline[];
  stages: PartnerPipelineStage[]; // stage della pipeline selezionata
  memberships: PartnerPipelineMembership[]; // membership della pipeline selezionata
  selectedPipelineId: string | null;
  loading: boolean;
  error: string | null;

  fetchPipelines: () => Promise<void>;
  selectPipeline: (pipelineId: string) => Promise<void>;
  moveToStage: (partnerId: string, stageId: string) => Promise<boolean>;
  addToPipeline: (partnerId: string, stageId: string) => Promise<boolean>;
  removeFromPipeline: (partnerId: string) => Promise<boolean>;
}

export const usePartnerPipelineStore = create<PartnerPipelineState>((set, get) => ({
  pipelines: [],
  stages: [],
  memberships: [],
  selectedPipelineId: null,
  loading: false,
  error: null,

  fetchPipelines: async () => {
    set({ loading: true, error: null });
    try {
      const { organizationId } = await getOrgAndUserId();

      const { data, error } = await supabase
        .from('partner_pipelines')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      set({ pipelines: data || [], loading: false });
    } catch (error) {
      console.error('[PartnerPipelineStore] Error fetching pipelines:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  selectPipeline: async (pipelineId: string) => {
    set({ selectedPipelineId: pipelineId, loading: true, error: null });
    try {
      const [stagesRes, membershipsRes] = await Promise.all([
        supabase
          .from('partner_pipeline_stages')
          .select('*')
          .eq('pipeline_id', pipelineId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('partner_pipeline_memberships')
          .select('*')
          .eq('pipeline_id', pipelineId),
      ]);

      if (stagesRes.error) throw stagesRes.error;
      if (membershipsRes.error) throw membershipsRes.error;

      set({
        stages: stagesRes.data || [],
        memberships: membershipsRes.data || [],
        loading: false,
      });
    } catch (error) {
      console.error('[PartnerPipelineStore] Error selecting pipeline:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  moveToStage: async (partnerId: string, stageId: string) => {
    set({ loading: true, error: null });
    try {
      const { organizationId, userId } = await getOrgAndUserId();
      const { selectedPipelineId, stages, memberships } = get();
      if (!selectedPipelineId) throw new Error('Nessuna pipeline selezionata');

      const oldMembership = memberships.find(m => m.partner_id === partnerId);
      const oldStageName = oldMembership
        ? stages.find(s => s.id === oldMembership.pipeline_stage_id)?.name
        : undefined;
      const newStageName = stages.find(s => s.id === stageId)?.name || 'N/D';

      const nowIso = new Date().toISOString();

      const { data, error } = await supabase
        .from('partner_pipeline_memberships')
        .update({ pipeline_stage_id: stageId, last_stage_change_at: nowIso })
        .eq('partner_id', partnerId)
        .eq('pipeline_id', selectedPipelineId)
        .select('*')
        .single();

      if (error) throw error;

      set(state => ({
        memberships: state.memberships.map(m => (m.id === data.id ? data : m)),
        loading: false,
      }));

      const { error: activityError } = await supabase.from('sales_activities').insert({
        partner_id: partnerId,
        organization_id: organizationId,
        user_id: userId,
        type: 'note',
        title: 'Cambio fase pipeline',
        description: `Fase: ${oldStageName || 'N/D'} → ${newStageName}`,
        status: 'completed',
        completed_at: nowIso,
        due_date: nowIso,
      });

      if (activityError) {
        console.warn('[PartnerPipelineStore] Error creating timeline row for moveToStage:', activityError);
      }

      return true;
    } catch (error) {
      console.error('[PartnerPipelineStore] Error moving partner to stage:', error);
      set({ error: friendlyDbError(error), loading: false });
      return false;
    }
  },

  addToPipeline: async (partnerId: string, stageId: string) => {
    set({ loading: true, error: null });
    try {
      const { organizationId, userId } = await getOrgAndUserId();
      const { selectedPipelineId, pipelines, stages } = get();
      if (!selectedPipelineId) throw new Error('Nessuna pipeline selezionata');

      const pipelineName = pipelines.find(p => p.id === selectedPipelineId)?.name || 'N/D';
      const stageName = stages.find(s => s.id === stageId)?.name || 'N/D';

      const nowIso = new Date().toISOString();

      const { data, error } = await supabase
        .from('partner_pipeline_memberships')
        .insert({
          partner_id: partnerId,
          pipeline_id: selectedPipelineId,
          pipeline_stage_id: stageId,
          organization_id: organizationId,
          entered_at: nowIso,
          last_stage_change_at: nowIso,
        })
        .select('*')
        .single();

      if (error) throw error;

      set(state => ({
        memberships: [...state.memberships, data],
        loading: false,
      }));

      const { error: activityError } = await supabase.from('sales_activities').insert({
        partner_id: partnerId,
        organization_id: organizationId,
        user_id: userId,
        type: 'note',
        title: 'Aggiunto alla pipeline',
        description: `Aggiunto alla pipeline ${pipelineName} — fase ${stageName}`,
        status: 'completed',
        completed_at: nowIso,
        due_date: nowIso,
      });

      if (activityError) {
        console.warn('[PartnerPipelineStore] Error creating timeline row for addToPipeline:', activityError);
      }

      return true;
    } catch (error) {
      console.error('[PartnerPipelineStore] Error adding partner to pipeline:', error);
      set({ error: friendlyDbError(error), loading: false });
      return false;
    }
  },

  removeFromPipeline: async (partnerId: string) => {
    set({ loading: true, error: null });
    try {
      const { selectedPipelineId } = get();
      if (!selectedPipelineId) throw new Error('Nessuna pipeline selezionata');

      const { error } = await supabase
        .from('partner_pipeline_memberships')
        .delete()
        .eq('partner_id', partnerId)
        .eq('pipeline_id', selectedPipelineId);

      if (error) throw error;

      set(state => ({
        memberships: state.memberships.filter(m => m.partner_id !== partnerId),
        loading: false,
      }));

      return true;
    } catch (error) {
      console.error('[PartnerPipelineStore] Error removing partner from pipeline:', error);
      set({ error: friendlyDbError(error), loading: false });
      return false;
    }
  },
}));
